import { Link } from "wouter";
import { AreaText } from "./area-text";
import { Ruler, PackageCheck, Wrench, Settings, Building2, Truck, Phone, ArrowRight } from "lucide-react";
import { PHONE_DISPLAY, PHONE_HREF, SECTORS, img, placeName, type LocationCtx } from "./location-data";

export function SellSupplyInstall({ ctx }: { ctx: LocationCtx }) {
  const where = placeName(ctx);
  const steps = [
    { icon: Ruler, n: "01", title: "We advise & design", desc: "Site surveys, room sizing, heat-load calculations and choosing the right system for domestic and commercial properties.", src: img("we-advise-design.webp"), alt: "VertexAir engineer advising a customer beside an air conditioning unit and branded van" },
    { icon: PackageCheck, n: "02", title: "We supply", desc: "Supply air-conditioning units and parts to homeowners, businesses and trade customers, including Daikin and Panasonic equipment.", src: img("we-supply-delivery.webp"), alt: "Wrapped equipment and parts loaded inside a delivery van" },
    { icon: Wrench, n: "03", title: "We install", desc: "Professional installation, commissioning, system testing and customer handover.", src: img("we-install-fujitsu.webp"), alt: "Engineer working on a Fujitsu Airstage outdoor air conditioning unit" },
    { icon: Settings, n: "04", title: "We maintain & repair", desc: "Servicing, routine maintenance, fault diagnosis and repairs to support customers after installation.", src: img("we-maintain-repair.webp"), alt: "Engineer testing electrical components in a ceiling-mounted ventilation unit" },
  ];
  return (
    <section className="py-10 md:py-14 bg-background">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">One specialist, start to finish</p>
        <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold max-w-3xl">From system design to ongoing care in <AreaText text={where} ctx={ctx} /></h2>
        <div className="mt-12 grid sm:grid-cols-2 xl:grid-cols-4 gap-6">
          {steps.map(({ icon: Icon, n, title, desc, src, alt }) => (
            <article key={n} className="group rounded-2xl overflow-hidden bg-card border border-border">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={src} alt={alt} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <span className="absolute top-4 left-4 font-display text-sm font-bold bg-white/90 text-foreground rounded-full px-3 py-1">{n}</span>
              </div>
              <div className="p-6">
                <h3 className="flex items-start gap-2 font-display text-xl font-bold"><Icon className="w-5 h-5 shrink-0 mt-1 text-primary" />{title}</h3>
                <p className="mt-2 text-muted-foreground">{desc}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export function LocalContent({ children, ctx }: { children: React.ReactNode; ctx: LocationCtx }) {
  return (
    <section className="py-10 md:py-14 bg-[hsl(210_40%_96%)] border-y border-border">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1fr_340px] gap-10">
        <article className="min-w-0 bg-card rounded-3xl border border-border p-6 md:p-10 shadow-sm">{children}</article>
        <aside className="space-y-5 lg:sticky lg:top-28 self-start">
          <figure className="rounded-2xl overflow-hidden border border-border">
            <img src={img("vertexair-team-vans.webp")} alt="VertexAir engineers with branded service vans outside the company premises" width={1536} height={1024} loading="lazy" className="w-full h-auto" />
            <figcaption className="bg-card p-4 text-sm text-muted-foreground">Engineers covering <AreaText text={placeName(ctx)} ctx={ctx} />{ctx.city.region ? ` and ${ctx.city.region.toLowerCase()}` : ""}.</figcaption>
          </figure>
          <div className="rounded-2xl bg-primary text-primary-foreground p-6">
            <p className="font-display text-xl font-bold">Talk to a specialist</p>
            <a href={PHONE_HREF} className="mt-3 flex items-center gap-2 text-2xl font-display font-bold hover:underline"><Phone className="w-5 h-5" />{PHONE_DISPLAY}</a>
            <Link href="/quote" className="mt-4 inline-flex w-full justify-center rounded-full bg-white text-primary px-5 py-3 font-semibold hover:bg-white/90">Request a quote</Link>
          </div>
        </aside>
      </div>
    </section>
  );
}

export function SectorsB2B({ ctx }: { ctx: LocationCtx }) {
  return (
    <section className="py-10 md:py-14 bg-background">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-end mb-10">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">Domestic, commercial and trade</p>
            <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold">Homes and businesses across <AreaText text={ctx.city.title} ctx={ctx} /></h2>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6 flex gap-4">
            <Building2 className="w-8 h-8 text-primary shrink-0" />
            <div>
              <h3 className="font-display text-lg font-bold">B2B and trade supply</h3>
              <p className="mt-1 text-muted-foreground text-sm">We work with business owners, facilities managers, contractors and trade buyers: equipment supply for your own installs, multi-site projects and full design-and-install packages.</p>
              <Link href="/contact" className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:gap-2 transition-all">Discuss a business project <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {SECTORS.map((s, i) => (
            <figure key={s.title} className={`relative rounded-2xl overflow-hidden group ${i === 0 ? "col-span-2 md:col-span-1" : ""}`}>
              <img src={s.src} alt={`${s.title} air conditioning installation`} loading="lazy" className="w-full aspect-[3/4] object-cover group-hover:scale-105 transition-transform duration-700" />
              <figcaption className="absolute inset-0 flex flex-col justify-end p-4 bg-gradient-to-t from-[hsl(222_47%_9%/0.9)] via-transparent text-white">
                <span className="font-display font-bold text-lg">{s.title}</span>
                <span className="text-xs text-white/80 hidden sm:block">{s.desc}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

export function DeliveryBand({ ctx }: { ctx: LocationCtx }) {
  return (
    <section className="relative overflow-hidden bg-[hsl(222_47%_11%)] text-white">
      <img src={img("commercial.webp")} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-25" />
      <div className="relative container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 md:py-14 grid md:grid-cols-[auto_1fr_auto] gap-8 items-center">
        <Truck className="w-14 h-14 text-sky-300" />
        <div>
          <h2 className="font-display text-3xl md:text-4xl font-bold">Guaranteed next-day delivery when stock is available</h2>
          <p className="mt-2 text-white/75 max-w-2xl">Equipment orders for <AreaText text={placeName(ctx)} ctx={ctx} /> are dispatched for next-day delivery whenever the units are in stock. This covers delivery of equipment only; installation is scheduled with you separately.</p>
        </div>
        <div className="flex flex-col gap-3">
          <Link href="/quote" className="inline-flex justify-center items-center gap-2 rounded-full bg-primary px-7 py-3.5 font-semibold hover:bg-primary/90">Get a free quote</Link>
          <Link href="/contact" className="inline-flex justify-center rounded-full border border-white/40 px-7 py-3.5 font-semibold hover:bg-white hover:text-foreground transition">Contact us</Link>
        </div>
      </div>
    </section>
  );
}
